<script setup lang="ts">
/**
 * Rahmen (Schattenfuge, Leiste, Passepartout, Posterleisten) um ein Druckformat-Rechteck.
 * Liegt absolut über dem Rechteck (Position per `style` vom Elternteil); die Zeichenfläche
 * ragt um den Rahmenüberstand hinaus. Gezeichnet wird mit drawFrame2d wie im Zuschnitt-Canvas.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { drawFrame2d, frame2dOverhangMm, type Frame2d } from '../../preview3d/flat2d'

const props = defineProps<{ frame: Frame2d }>()

const host = ref<HTMLElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const ext = ref({ x: 0, y: 0 })
let ro: ResizeObserver | null = null
let raf = 0

function draw() {
  cancelAnimationFrame(raf)
  raf = requestAnimationFrame(() => {
    const el = host.value
    const c = canvas.value
    if (!el || !c) return
    const w = el.clientWidth
    const h = el.clientHeight
    if (!(w > 4) || !(h > 4)) return
    const k = w / props.frame.printW
    const o = frame2dOverhangMm(props.frame)
    const ex = Math.ceil(o.x * k) + 2
    const ey = Math.ceil(o.y * k) + 2
    ext.value = { x: ex, y: ey }
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const cw = w + 2 * ex
    const ch = h + 2 * ey
    c.width = Math.round(cw * dpr)
    c.height = Math.round(ch * dpr)
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, cw, ch)
    drawFrame2d(ctx, { x: ex, y: ey, w, h }, props.frame, draw)
  })
}

onMounted(() => {
  if (host.value && typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => draw())
    ro.observe(host.value)
  }
  draw()
})
watch(() => JSON.stringify(props.frame), () => draw())
onBeforeUnmount(() => {
  ro?.disconnect()
  cancelAnimationFrame(raf)
})
</script>

<template>
  <div ref="host" class="frame2d" aria-hidden="true">
    <canvas
      ref="canvas"
      class="frame2d__canvas"
      :style="{ left: `${-ext.x}px`, top: `${-ext.y}px`, width: `calc(100% + ${2 * ext.x}px)`, height: `calc(100% + ${2 * ext.y}px)` }"
    />
  </div>
</template>

<style scoped>
.frame2d { position: absolute; pointer-events: none; overflow: visible; z-index: 1; }
.frame2d__canvas { position: absolute; display: block; }
</style>
